import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { vsprintf } from "sprintf-js";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: {
    margin: theme.spacing(2),
    marginTop: 35,
  },
  paper: {
    padding: theme.spacing(3),
    textAlign: "center",
  },
  successBox: {
    background: "#e8f5e9",
    border: "2px solid #4caf50",
    borderRadius: 8,
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  completionCodeBox: {
    background: "#fff3cd",
    border: "2px solid #ffc107",
    borderRadius: 8,
    padding: theme.spacing(3),
    margin: theme.spacing(2, 0),
    textAlign: "center",
  },
  completionCode: {
    fontSize: 32,
    fontWeight: "bold",
    letterSpacing: 6,
    color: "#2e7d32",
    fontFamily: "'Courier New', monospace",
  },
  errorBox: {
    background: "#ffebee",
    border: "2px solid #f44336",
    borderRadius: 8,
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  cancelledBox: {
    background: "#fff3e0",
    border: "2px solid #ff9800",
    borderRadius: 8,
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  button: {
    margin: theme.spacing(1),
    fontSize: "13pt",
  },
  "@media (min-width: 1024px)": {
    root: {
      width: 700,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 90,
    },
  },
});

class DepositPaymentPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = { mounted: false };
  }

  componentDidMount() {
    this.setState({ mounted: true });

    const path = window.location.pathname;

    if (path === "/depositPaymentSuccess") {
      const params = new URLSearchParams(window.location.search);
      const uniqueID = params.get("uniqueID");
      const personalId = params.get("personalId");
      const cardMask = params.get("cardMask");
      const txId = params.get("txId");
      const authNumber = params.get("authNumber");
      const responseMAC = params.get("responseMAC");

      const traceId =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

      this.props.depositActions.finalizeDeposit({
        personalId,
        cardMask,
        txId,
        authNumber,
        responseMAC,
        uniqueID,
        traceId,
      });
    }
  }

  render() {
    const { classes, depositReducer } = this.props;
    const { finalizeResult, loading } = depositReducer;

    // During SSR, render a simple loading state to avoid window is not defined
    if (!this.state.mounted) {
      return (
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <CircularProgress size={50} />
          </Paper>
        </div>
      );
    }

    const path = window.location.pathname;

    // Loading
    if (path === "/depositPaymentSuccess" && !finalizeResult) {
      return (
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <CircularProgress size={50} />
            <Typography variant="h6" style={{ marginTop: 16 }}>
              מעבד את התשלום...
            </Typography>
          </Paper>
        </div>
      );
    }

    // Success
    if (path === "/depositPaymentSuccess" && finalizeResult && finalizeResult.ok) {
      return (
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <div className={classes.successBox}>
              <Typography variant="h5">
                תשלום המקדמה התקבל בהצלחה!
              </Typography>
              <Typography variant="body1" style={{ marginTop: 8 }}>
                מספר הזמנה: <strong>{finalizeResult.orderId}</strong>
              </Typography>
            </div>

            <div className={classes.completionCodeBox}>
              <Typography variant="body1" gutterBottom>
                <strong>קוד להשלמת התשלום:</strong>
              </Typography>
              <Typography className={classes.completionCode}>
                {finalizeResult.completionCode}
              </Typography>
              <Typography
                variant="body2"
                style={{ marginTop: 8, color: "#856404" }}
              >
                שמרו את הקוד הזה! תצטרכו אותו כדי להשלים את התשלום לפני המשלוח.
              </Typography>
            </div>

            <Typography variant="body1" style={{ margin: "12px 0" }}>
              מקדמה ששולמה:{" "}
              <strong>
                {vsprintf("%.2f", [finalizeResult.depositAmount])} ₪
              </strong>
            </Typography>
            <Typography variant="body1">
              יתרה לתשלום:{" "}
              <strong>
                {vsprintf("%.2f", [finalizeResult.remainingAmount])} ₪
              </strong>
            </Typography>

            <div style={{ marginTop: 24 }}>
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={() => (window.location.href = "/complete-payment")}
              >
                השלמת תשלום
              </Button>
              <Button
                variant="outlined"
                className={classes.button}
                onClick={() => (window.location.href = "/")}
              >
                חזרה לחנות
              </Button>
            </div>
          </Paper>
        </div>
      );
    }

    // Failed
    if (path === "/depositPaymentFailed") {
      return (
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <div className={classes.errorBox}>
              <Typography variant="h5">התשלום נכשל</Typography>
              <Typography variant="body1" style={{ marginTop: 8 }}>
                חיוב המקדמה לא בוצע. אנא נסו שנית.
              </Typography>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={() => (window.location.href = "/deposit-checkout")}
            >
              נסה שנית
            </Button>
          </Paper>
        </div>
      );
    }

    // Cancelled
    if (path === "/depositPaymentCanceled") {
      return (
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <div className={classes.cancelledBox}>
              <Typography variant="h5">התשלום בוטל</Typography>
              <Typography variant="body1" style={{ marginTop: 8 }}>
                ביטלת את תהליך התשלום.
              </Typography>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={() => (window.location.href = "/deposit-checkout")}
            >
              חזרה לעגלה
            </Button>
          </Paper>
        </div>
      );
    }

    // Fallback / error
    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <div className={classes.errorBox}>
            <Typography variant="h5">שגיאה</Typography>
            <Typography variant="body1" style={{ marginTop: 8 }}>
              {finalizeResult && finalizeResult.reason
                ? finalizeResult.reason
                : "אירעה שגיאה בעיבוד התשלום."}
            </Typography>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={() => (window.location.href = "/")}
          >
            חזרה לחנות
          </Button>
        </Paper>
      </div>
    );
  }
}

DepositPaymentPage.propTypes = {
  classes: PropTypes.object.isRequired,
  depositReducer: PropTypes.object.isRequired,
  depositActions: PropTypes.object.isRequired,
};

export default withStyles(styles)(DepositPaymentPage);
